import { useEffect, useState, useReducer } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { PageHeader } from '@/components/common/PageHeader'
import type { Tables } from '@/lib/database.types'
import css from '@/components/common/Common.module.css'

type Product = Tables<'products'>
type ProductOption = Tables<'product_options'>
type PricingTable = Tables<'product_pricing_tables'>

interface PricingRow {
  projection_cm: number
  prices_gbp: (number | null)[]
  motor_nm?: number
  minimum_width_cm?: number
}

interface ConfigState {
  width: number
  height: number
  projection: number
  selectedOptions: Record<string, string>
  quantity: number
}

type ConfigAction =
  | { type: 'SET_DIMENSION'; field: 'width' | 'height' | 'projection'; value: number }
  | { type: 'SET_OPTION'; group: string; optionId: string }
  | { type: 'SET_QUANTITY'; value: number }
  | { type: 'INIT'; product: Product; options: ProductOption[] }

function configReducer(state: ConfigState, action: ConfigAction): ConfigState {
  switch (action.type) {
    case 'SET_DIMENSION':
      return { ...state, [action.field]: action.value }
    case 'SET_OPTION':
      return { ...state, selectedOptions: { ...state.selectedOptions, [action.group]: action.optionId } }
    case 'SET_QUANTITY':
      return { ...state, quantity: action.value }
    case 'INIT': {
      const defaults: Record<string, string> = {}
      action.options.filter(o => o.is_default).forEach(o => { defaults[o.group_name] = o.id })
      return {
        width: action.product.min_width_mm || 1000,
        height: action.product.min_height_mm || 1000,
        projection: action.product.min_projection_mm || 0,
        selectedOptions: defaults,
        quantity: 1,
      }
    }
    default: return state
  }
}

/** Look up price from a width×projection pricing grid */
function lookupGridPrice(pricingTables: PricingTable[], widthMm: number, projectionMm: number): number | null {
  if (pricingTables.length === 0) return null

  const widthCm = widthMm / 10
  const projectionCm = projectionMm / 10

  // Use first pricing table (sorted by sort_order from DB)
  const table = pricingTables[0]
  const widthColumns = table.width_columns_cm
  const rows = table.rows as unknown as PricingRow[]

  if (!rows || !Array.isArray(rows) || rows.length === 0) return null

  // Find best projection row (nearest ≤ value)
  let bestRow: PricingRow | null = null
  for (const row of rows) {
    if (row.projection_cm <= projectionCm) {
      if (!bestRow || row.projection_cm > bestRow.projection_cm) {
        bestRow = row
      }
    }
  }
  if (!bestRow) bestRow = rows[0] // fallback to smallest projection

  // Find best width column (nearest ≤ value)
  let colIndex = -1
  for (let i = 0; i < widthColumns.length; i++) {
    if (widthColumns[i] <= widthCm) colIndex = i
  }
  if (colIndex === -1) colIndex = 0 // fallback to smallest width

  const price = bestRow.prices_gbp[colIndex]
  return price ?? null
}

export function Configurator() {
  const { productId } = useParams<{ productId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [product, setProduct] = useState<Product | null>(null)
  const [options, setOptions] = useState<ProductOption[]>([])
  const [pricingTables, setPricingTables] = useState<PricingTable[]>([])
  const [loading, setLoading] = useState(true)
  const [state, dispatch] = useReducer(configReducer, { width: 1000, height: 1000, projection: 0, selectedOptions: {}, quantity: 1 })

  useEffect(() => {
    if (!productId) return
    async function load() {
      const [prodRes, optsRes, pricingRes] = await Promise.all([
        supabase.from('products').select('*').eq('id', productId!).single(),
        supabase.from('product_options').select('*').eq('product_id', productId!).eq('is_active', true).order('group_name').order('sort_order'),
        supabase.from('product_pricing_tables').select('*').eq('product_id', productId!).order('sort_order'),
      ])
      if (prodRes.data) {
        setProduct(prodRes.data)
        setOptions(optsRes.data || [])
        setPricingTables(pricingRes.data || [])
        dispatch({ type: 'INIT', product: prodRes.data, options: optsRes.data || [] })
      }
      setLoading(false)
    }
    load()
  }, [productId])

  if (loading) return <p style={{ color: 'var(--color-text-muted)', padding: 'var(--space-8)' }}>Loading configurator...</p>
  if (!product) return <p>Product not found</p>

  const optionGroups = options.reduce<Record<string, ProductOption[]>>((acc, opt) => {
    if (!acc[opt.group_name]) acc[opt.group_name] = []
    acc[opt.group_name].push(opt)
    return acc
  }, {})

  // Validation
  const errors: string[] = []
  const warnings: string[] = []

  if (product.min_width_mm && state.width < product.min_width_mm) errors.push(`Width must be at least ${product.min_width_mm}mm`)
  if (product.max_width_mm && state.width > product.max_width_mm) errors.push(`Width must not exceed ${product.max_width_mm}mm`)
  if (product.min_height_mm && state.height < product.min_height_mm) errors.push(`Height must be at least ${product.min_height_mm}mm`)
  if (product.max_height_mm && state.height > product.max_height_mm) errors.push(`Height must not exceed ${product.max_height_mm}mm`)
  if (product.min_projection_mm && state.projection < product.min_projection_mm) errors.push(`Projection must be at least ${product.min_projection_mm}mm`)
  if (product.max_projection_mm && state.projection > product.max_projection_mm) errors.push(`Projection must not exceed ${product.max_projection_mm}mm`)

  // Calculate price from pricing grid (fallback to base_price)
  const gridPrice = lookupGridPrice(pricingTables, state.width, state.projection)
  let price = gridPrice ?? (Number(product.base_price) || 0)

  // Option price deltas and surcharges
  let hasNonStock = false
  let surchargeTotal = 0
  Object.values(state.selectedOptions).forEach(optId => {
    const opt = options.find(o => o.id === optId)
    if (!opt) return
    if (opt.price_delta) price += Number(opt.price_delta)
    if (opt.surcharge_percent && Number(opt.surcharge_percent) > 0) {
      const surcharge = price * (Number(opt.surcharge_percent) / 100)
      surchargeTotal += surcharge
    }
    if (opt.in_stock === false) hasNonStock = true
  })
  price += surchargeTotal

  if (hasNonStock) {
    warnings.push('Non-stock colour selected — extended lead time may apply')
  }

  const unitPrice = Math.round(price * 100) / 100
  const lineTotal = unitPrice * state.quantity

  const isValid = errors.length === 0

  const handleAddToQuote = () => {
    const config = {
      productId: product.id,
      productName: product.name,
      sku: product.sku,
      dimensions: { width: state.width, height: state.height, projection: state.projection },
      selectedOptions: Object.entries(state.selectedOptions).map(([group, optId]) => {
        const opt = options.find(o => o.id === optId)
        return { group, optionId: optId, optionName: opt?.name, priceDelta: opt?.price_delta }
      }),
      quantity: state.quantity,
      unitPrice,
      lineTotal,
    }
    const projectId = searchParams.get('project')
    const params = new URLSearchParams({ config: JSON.stringify(config) })
    if (projectId) params.set('project', projectId)
    navigate(`/gaviota/quotes/new?${params.toString()}`)
  }

  return (
    <div>
      <PageHeader title={`Configure ${product.name}`} description={product.sku} />

      <div className={css.grid2}>
        <div>
          <div className={css.card} style={{ marginBottom: 'var(--space-4)' }}>
            <h3 style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--space-5)' }}>Dimensions</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Width (mm) {product.min_width_mm && <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>({product.min_width_mm}–{product.max_width_mm})</span>}</label>
                <input type="number" className={css.formInput} value={state.width} onChange={e => dispatch({ type: 'SET_DIMENSION', field: 'width', value: Number(e.target.value) })} min={product.min_width_mm || 0} max={product.max_width_mm || 99999} />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Height (mm) {product.min_height_mm && <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>({product.min_height_mm}–{product.max_height_mm})</span>}</label>
                <input type="number" className={css.formInput} value={state.height} onChange={e => dispatch({ type: 'SET_DIMENSION', field: 'height', value: Number(e.target.value) })} min={product.min_height_mm || 0} max={product.max_height_mm || 99999} />
              </div>
              {(product.min_projection_mm || product.max_projection_mm) && (
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Projection (mm) <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>({product.min_projection_mm}–{product.max_projection_mm})</span></label>
                  <input type="number" className={css.formInput} value={state.projection} onChange={e => dispatch({ type: 'SET_DIMENSION', field: 'projection', value: Number(e.target.value) })} min={product.min_projection_mm || 0} max={product.max_projection_mm || 99999} />
                </div>
              )}
              <div className={css.formGroup}>
                <label className={css.formLabel}>Quantity</label>
                <input type="number" className={css.formInput} value={state.quantity} onChange={e => dispatch({ type: 'SET_QUANTITY', value: Math.max(1, Number(e.target.value)) })} min={1} />
              </div>
            </div>
          </div>

          {Object.entries(optionGroups).map(([group, opts]) => (
            <div key={group} className={css.card} style={{ marginBottom: 'var(--space-4)' }}>
              <h3 style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }}>{group.replace(/_/g, ' ')}</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                {opts.map(opt => (
                  <label key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', border: `1px solid ${state.selectedOptions[group] === opt.id ? 'var(--color-primary)' : 'var(--color-border-subtle)'}`, background: state.selectedOptions[group] === opt.id ? 'var(--color-primary-muted)' : 'transparent', cursor: 'pointer', transition: 'all 150ms ease' }}>
                    <input type="radio" name={group} checked={state.selectedOptions[group] === opt.id} onChange={() => dispatch({ type: 'SET_OPTION', group, optionId: opt.id })} style={{ accentColor: 'var(--color-primary)' }} />
                    <span style={{ flex: 1, fontSize: 'var(--text-sm)' }}>
                      {opt.name}
                      {opt.in_stock === false && (
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-technical)', marginLeft: 'var(--space-2)' }}>Non-stock</span>
                      )}
                    </span>
                    <span style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                      {opt.price_delta !== null && Number(opt.price_delta) !== 0 && (
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-mono)' }}>+£{Number(opt.price_delta).toLocaleString()}</span>
                      )}
                      {opt.surcharge_percent !== null && Number(opt.surcharge_percent) > 0 && (
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-technical)', fontFamily: 'var(--font-mono)' }}>+{Number(opt.surcharge_percent)}%</span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div>
          <div className={css.card} style={{ position: 'sticky', top: 'calc(var(--topbar-height) + var(--space-8))' }}>
            <h3 style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--space-5)' }}>Configuration Summary</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-5)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)' }}>Product</span><span style={{ fontWeight: 500 }}>{product.name}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)' }}>Dimensions</span><span>{state.width} x {state.height} mm</span></div>
              {state.projection > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)' }}>Projection</span><span>{state.projection} mm</span></div>}
              {Object.entries(state.selectedOptions).map(([group, optId]) => {
                const opt = options.find(o => o.id === optId)
                return opt ? (
                  <div key={group} style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)', textTransform: 'capitalize' }}>{group.replace(/_/g, ' ')}</span><span>{opt.name}</span></div>
                ) : null
              })}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)' }}>Quantity</span><span>{state.quantity}</span></div>
              {gridPrice !== null && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)' }}>Pricing</span><span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-success)' }}>Grid lookup</span></div>
              )}
            </div>

            <div style={{ borderTop: '1px solid var(--color-border-subtle)', paddingTop: 'var(--space-4)', marginBottom: 'var(--space-5)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Unit Price</span>
                <span style={{ fontSize: 'var(--text-sm)' }}>£{unitPrice.toLocaleString()}</span>
              </div>
              {surchargeTotal > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-technical)' }}>Includes non-stock surcharge</span>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-technical)', fontFamily: 'var(--font-mono)' }}>+£{Math.round(surchargeTotal * 100 / 100).toLocaleString()}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 'var(--text-lg)', fontWeight: 600 }}>Total</span>
                <span style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--color-primary-dark)' }}>£{lineTotal.toLocaleString()}</span>
              </div>
            </div>

            {errors.length > 0 && (
              <div style={{ background: 'var(--color-error-muted)', border: '1px solid rgba(194,59,59,0.2)', borderRadius: 'var(--radius-md)', padding: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                {errors.map((e, i) => <p key={i} style={{ fontSize: 'var(--text-xs)', color: 'var(--color-error)' }}>{e}</p>)}
              </div>
            )}

            {warnings.length > 0 && (
              <div style={{ background: 'var(--color-technical-muted)', border: '1px solid rgba(199,123,26,0.2)', borderRadius: 'var(--radius-md)', padding: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                {warnings.map((w, i) => <p key={i} style={{ fontSize: 'var(--text-xs)', color: 'var(--color-technical)' }}>{w}</p>)}
              </div>
            )}

            <button className={css.btnPrimary} style={{ width: '100%', justifyContent: 'center' }} onClick={handleAddToQuote} disabled={!isValid}>
              Add to Quote
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
